import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import axios, { AxiosRequestConfig } from "axios";
import ProductFilter from "@/components/ProductSelector/ProductFilter.vue";
import ProductVariants from "@/components/VariantsTable/ProductVariants.vue";
import ProductMatrix from "@/components/VariantsTable/ProductMatrix.vue";
import DownloadDocument from "@/components/DownloadDocument.vue";
import Loader from "@/components/utils/Loader.vue";
import { Range } from "@/models/Range";
import {
  FilterTemplate,
  FilterValue,
  FilterRangeValue,
  FilterSingleValue
} from "@/models/Filter";
import { Column } from "@/models/Column";
import { Rule } from "@/models/Rule";
import { matPrint } from "@quasar/extras/material-icons";
import { sleep } from "@/utils/sleep";
import { isIEorEdge } from "@/utils/isIEorEdge";
import { translate } from "@/utils/translation";
declare const window: any;

@Component({
  components: {
    ProductFilter,
    ProductVariants,
    ProductMatrix,
    DownloadDocument,
    Loader
  }
})
export default class ProductSelector extends Vue {
  @Prop() private queryUrl!: string;
  @Prop() private language!: string;
  @Prop() private variantsType!: string;
  @Prop() private config!: any;
  @Prop() private selectedTypes!: Array<string>;
  
  public store = this.$store.direct

  private headline!: string;
  private image!: string;
  private matPrint: string | undefined;
  private matCancel: string | undefined;

  private filters: Array<FilterTemplate> = [];
  private columns: Array<Column> = [];
  private rules: Array<Rule> = [];
  private productMatrixAxis: string[] = ['ItemHeight', 'ItemWidth'];

  private variants: Array<any> = [];
  private afterFirstQuery = false;
  private variantsScrollEnable = true;

  public created() {
    this.store.dispatch.appStateModule.setLoaderOn();
    this.matPrint = matPrint;
    if(isIEorEdge()) {
      this.variantsScrollEnable = false;
    }

    if (this.config && Object.keys(this.config).length > 0 && !Array.isArray(this.config)) {
      this.configure(this.config);
      this.afterFirstQuery = true;
    }
    else {
      this.fetchFilter(this.store.state.selectorStateModule.selectedProduct);
    }
  }

  @Watch('config')
  public onConfigChange(val: any) {
    this.variants = []
    this.configure(val);
    this.fetchData();
  }

  public configure(config: any) {
    this.headline = config.Title;
    this.image = config.EntityImage;

    this.productMatrixAxis = [config.Fields.find((x: any) => x.FieldName == 'ItemHeight')?.Title]
    this.productMatrixAxis.push(config.Fields.find((x: any) => x.FieldName == 'ItemWidth')?.Title);
    const url = new URL(window.location.href);
    if(!this.afterFirstQuery) {
      this.initFilters(Number(url.searchParams.get('heatoutput')), Number(url.searchParams.get('flowtemp')), Number(url.searchParams.get('returntemp')), Number(url.searchParams.get('roomtemp')));
    }
    else {
      this.initFilters();
    }
    this.initColumns();
    this.initRules();
  }


  public mounted() {
    this.fetchData();
  }

  public initFilters(initialHeatOutput: number | undefined = undefined , initialFlowTemp: number | undefined = undefined , initialReturnTemp: number | undefined = undefined, initialRoomTemp: number | undefined = undefined) {
    const inlineFilters = this.store.state.selectorStateModule.filters as Array<FilterTemplate>;
    const newFilters: FilterTemplate[] = [];
    inlineFilters.map(x => {
      const filter = { ...x } as FilterTemplate;

      if (filter.MinValue === null) {
        filter.MinValue = 0;
      }
      if (filter.MaxValue === null) {
        filter.MaxValue = 100;
      }

      const existingFilter = this.filters.find(f => f.Id === filter.Id);
      if (filter.IsRange) {
        const defaultRange = filter.DefaultValue?.split(",").map(x => {
          return parseInt(x);
        });

        if (existingFilter !== undefined && this.checkFilterInRange((existingFilter?.Value as Range), filter.MinValue, filter.MaxValue)) {
          filter.Value = existingFilter.Value;
        }
        else if (defaultRange?.length == 2 && this.checkFilterInRange(new Range(defaultRange[0], defaultRange[1]), filter.MinValue, filter.MaxValue)) {
          filter.Value = new Range(defaultRange[0], defaultRange[1]);
        } else {
          filter.Value = new Range(filter.MinValue, filter.MaxValue);
        }
      } else {
        if(existingFilter !== undefined && this.isInRange(Number(existingFilter.Value), filter.MinValue, filter.MaxValue)) {
          filter.Value = existingFilter.Value
        }
        else {
          if(filter.FieldName === 'W_output' && initialHeatOutput !== undefined && this.isInRange(initialHeatOutput, filter.MinValue, filter.MaxValue)) {
            filter.Value = initialHeatOutput;
          }
          else if(filter.FieldName === 'T_flow' && initialFlowTemp !== undefined && this.isInRange(initialFlowTemp, filter.MinValue, filter.MaxValue)){
            filter.Value = initialFlowTemp;
          }
          else if(filter.FieldName === 'T_rtn' && initialReturnTemp !== undefined && this.isInRange(initialReturnTemp, filter.MinValue, filter.MaxValue)){
            filter.Value = initialReturnTemp;
          }
          else if(filter.FieldName === 'T_room' && initialRoomTemp !== undefined && this.isInRange(initialRoomTemp, filter.MinValue, filter.MaxValue)){
            filter.Value = initialRoomTemp;
          }
          else {
            filter.Value = filter.DefaultValue
            ? parseInt(filter.DefaultValue)
            : filter.MaxValue / 2;
          }
        }
      }

      newFilters.push(filter);
    });
    this.filters = newFilters;
    this.store.commit.selectorStateModule.SET_FILTERS(newFilters);
  }


  public initColumns() {
    this.columns = [];
    const inlineFields = this.config.Fields as Array<any>;

    inlineFields.map(x => {
      const c = new Column();
      c.field = x.FieldName;
      c.label = x.Title;
      c.displayLabel = c.label;
      c.name = x.FieldName;

      if (x.Hidden !== true) {
        this.columns.push(c);
      }
    });
  }

  public initRules() {
    this.rules = [];
    const inlineRules = this.config.Rules as Array<any>;
    inlineRules.map(rule => {
      const left = this.filters.find(x => x.FieldName === rule.Left);
      const right = this.filters.find(x => x.FieldName === rule.Right);
      if (left != undefined && right != undefined) {
        this.rules.push(
          new Rule(left, rule.Operator, right, rule.DiffMin, rule.DiffMax)
        );
      } else {
        console.warn(`Rule: can't find one of filter definitions for ${rule}.`);
      }
    });
  }

  public GetValue(ft: FilterTemplate): FilterValue {
    if (ft.IsRange) {
      return new FilterRangeValue(
        ft.Id,
        ft.FieldName,
        (ft.Value as Range).min,
        (ft.Value as Range).max
      );
    } else {
      return new FilterSingleValue(ft.Id, ft.FieldName, ft.Value as number);
    }
  }

  public fetchFilter(pageId: string) {
    this.store.dispatch.appStateModule.setLoaderOn();
    axios
      .get(
        this.queryUrl +
        "/GetProductFilter?pageId=" +
        pageId +
        "&lang=" +
        this.language
      )
      .then(response => {
        this.store.dispatch.selectorStateModule.initConfig({config: response.data});
        this.afterFirstQuery = true;
        this.store.dispatch.appStateModule.setLoaderOff();
      });
  }

  public fetchData() {
    if (this.config === undefined) {
      return;
    }

    const filterValues = this.filters.map(x => this.GetValue(x));

    this.queryData(filterValues)
  }

  public fetchAllItems() {
    if (this.config === undefined) {
      return;
    }

    const necesseryFilters = ['T_flow', 'T_rtn', 'T_room', 'W_output']
    const filterValues = this.filters.filter(x => necesseryFilters.includes(x.FieldName)).map(x => this.GetValue(x))

    this.queryData(filterValues, true)
  }

  public queryData(filters: FilterValue[], showAll = false) {
    this.store.dispatch.appStateModule.setLoaderOn();

    const query = {
      Brand: this.config.Brand,
      PageId: this.config.PageId,
      QueryId: this.config.QueryId,
      Filters: filters,
      Language: this.language,
      showAllItems: showAll
    };

    const customHeaders: AxiosRequestConfig = {
      headers: {
        "content-type": "application/json"
      }
    };

    axios
      .post(this.queryUrl + "/Query", query, customHeaders)
      .then(response => {
        this.variants = response.data.Items;
        this.store.dispatch.appStateModule.setLoaderOff();
      })
      .catch(() => this.store.dispatch.appStateModule.setLoaderOff());
  }

  private async print() {
    this.variantsScrollEnable = false;
    await sleep(50);
    const array = (this.$refs.variantTable as Array<Element>).map(element => {
      const clone = element.cloneNode(true);
      const filesElemArray = [].slice.call((clone as Element).getElementsByClassName('files'));
      filesElemArray.forEach((file: Element) => file.remove())
      return clone as Element;
    })
    let printOutput = '';
    array.forEach(element => printOutput = printOutput.concat(element.innerHTML));
    const WinPrint = window.open('', '', 'width=800,height=900');
    WinPrint.document.write(`<link rel="stylesheet" type="text/css" href="${process.env.VUE_APP_PRINT_CSS_FILE_PATH}" media="all">`)
    WinPrint.document.write(printOutput);
    WinPrint.document.close();
    WinPrint.focus();
    await sleep(200);
    WinPrint.print();
    if(!isIEorEdge()) {
      this.variantsScrollEnable = true;
    }
  }

  private variantsByTypeItems(type: string): Array<any> {
    if (this.variants) {
      return this.variants.filter(x => x.itemtype == type);
    }
    return [];
  }

  private variantsByTypeTitle(type: string): string {
    return `${this.Translate("Type")} ${type}`
  }

  private get hasResults(): boolean {
    return this.variants && this.variants.length > 0;
  }

  private get ShowFilters() {
    return this.filters.length > 0;
  }

  private get FirstTwoFilters(): FilterTemplate[] {
    return this.filters.slice(0, 2);
  }

  private get FiltersWithoutFirstTwo(): FilterTemplate[] {
    return this.filters.slice(2);
  }

  private OrphanColumn(index: number, colCount = 3) {
    const len = this.FiltersWithoutFirstTwo.length;
    return index >= Math.floor(len / colCount) * colCount;
  }

  private get SelectedTypesSorted() {
    return this.selectedTypes.sort();
  }

  private Translate(text: string): string {
    return translate(text);
  }

  private isInRange(value: number, rangeMin: number, rangeMax: number) {
    if(rangeMin <= value && rangeMax >= value)
      return true;
    return false;
  }

  private checkFilterInRange(filterValue: Range, rangeMin: number, rangeMax: number): boolean {
    if(filterValue === undefined)
      return false;
    return this.isInRange(filterValue.max, rangeMin, rangeMax) && this.isInRange(filterValue.min, rangeMin, rangeMax)
  }

  onFilterChanged(filter: FilterTemplate) {
    const rule: Rule | undefined = this.rules.find(
      x => x.Left == filter || x.Right == filter
    );

    if (rule !== undefined) {
      const matchOnLeft = rule.Left.FieldName == filter.FieldName;
      const leftF = rule.Left;
      const rightF = rule.Right;

      const leftV = leftF.Value as number;
      const rightV = rightF.Value as number;

      switch (rule.Opertator) {
        case "<":
          if (leftV >= rightV) {
            if (matchOnLeft) {
              rightF.Value = (leftF.Value as number) + rule.DiffMin;
            } else {
              leftF.Value = (rightF.Value as number) - rule.DiffMin;
            }
          }
          break;
        case ">":
          if (leftV <= rightV) {
            if (matchOnLeft) {
              rightF.Value = (leftF.Value as number) - rule.DiffMin;
            } else {
              leftF.Value = (rightF.Value as number) + rule.DiffMin;
            }
          } else {
            if (rule.DiffMax > 0 && leftV - rightV > rule.DiffMax) {
              if (matchOnLeft) {
                rightF.Value = (leftF.Value as number) - rule.DiffMax;
              } else {
                leftF.Value = (rightF.Value as number) + rule.DiffMax;
              }
            }
          }
          break;
        case "=":
          rightF.Value = leftF.Value;
          break;
      }
    }

    this.fetchData();
  }

  @Watch("store.state.selectorStateModule.selectedProduct")
  onTypesChange(product: any) {
    if (product) {
      this.fetchFilter(product);
    }
  }
}
